import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-order-card',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, MatIconModule, MatButtonModule],
  templateUrl: './order-card.component.html',
  styleUrls: ['./order-card.component.css']
})
export class OrderCardComponent {
  @Input() orderId: string | number = '';
  @Input() date: string | Date = '';
  @Input() tableName: string = '';
  @Input() tableType?: string;
  @Input() customerName?: string;
  @Input() status: string = '';
  @Input() total: number = 0;
  @Input() itemSummary: string = '';
  @Input() itemCounts?: { pending: number; sent: number; ready: number; delivered: number; canceled: number };
  @Input() productSummary?: string;
  @Input() paymentSummary?: { paid: number; pending: number; methods: string[] };
  @Input() priceSummary?: { total: number; discount: number; tip: number; payable: number; delivery?: number };
  @Input() items?: Array<{ name: string; qty: number; price?: number; status?: string; options?: Array<{ name: string; qty: number }>; lineTotal?: number }>;

  itemStatusLabel(s: string | undefined | null): string {
    const v = (s || '').toLowerCase();
    if (v === 'pending') return 'Pend.';
    if (v === 'sent') return 'Env.';
    if (v === 'ready') return 'List.';
    if (v === 'delivered') return 'Ent.';
    if (v === 'canceled') return 'Canc.';
    return (v || 'Pend.');
  }

  @Input() isAdmin: boolean = false;

  @Output() clicked = new EventEmitter<void>();
  @Output() deleted = new EventEmitter<void>();
  @Output() editClicked = new EventEmitter<void>();

  onClick() {
    this.clicked.emit();
  }

  onDelete(event: MouseEvent) {
    event.stopPropagation();
    this.deleted.emit();
  }

  onEdit(event: MouseEvent) {
    event.stopPropagation();
    this.editClicked.emit();
  }

  get statusClass(): string {
    return (this.status || '').toLowerCase().replace(/\s+/g, '-');
  }

  get tooltipText(): string | null {
    if (!this.itemCounts) return null;
    const c = this.itemCounts;
    return `Pendientes: ${c.pending} • Enviados: ${c.sent} • Listos: ${c.ready} • Entregados: ${c.delivered} • Cancelados: ${c.canceled}`;
  }

  // Delivered fraction n/m where m excludes canceled
  get deliveredFraction(): string | null {
    if (!this.itemCounts) return null;
    const c = this.itemCounts;
    const nonCanceledTotal = (c.pending || 0) + (c.sent || 0) + (c.ready || 0) + (c.delivered || 0);
    const delivered = c.delivered || 0;
    if (nonCanceledTotal <= 0) return '0/0';
    return `${delivered}/${nonCanceledTotal}`;
  }

  get canceledCount(): number {
    return this.itemCounts?.canceled || 0;
  }

  get hasPayment(): boolean {
    // Consider payment available if we can compute payable/pending
    return this.payable >= 0;
  }

  get paymentMethodsLabel(): string {
    const m = this.paymentSummary?.methods || [];
    if (!m.length) return '';
    return m.map(x => {
      const v = (x || '').toLowerCase();
      if (v === 'cash') return 'Efectivo';
      if (v === 'card') return 'Tarjeta';
      if (v === 'agreement') return 'Convenio';
      if (v === 'transferencia' || v === 'transfer') return 'Transferencia';
      return v.charAt(0).toUpperCase() + v.slice(1);
    }).join(' • ');
  }

  // Payment amounts for footer
  get paid(): number {
    return Number(this.paymentSummary?.paid || 0);
  }

  get payable(): number {
    const ps = this.priceSummary;
    const calc = Number(ps?.payable ?? ((Number(ps?.total || 0) - Number(ps?.discount || 0) + Number(ps?.tip || 0))));
    return isNaN(calc) ? 0 : calc;
  }

  get pending(): number {
    const p = (this.payable - this.paid);
    return isNaN(p) ? 0 : p;
  }

  get isDelivery(): boolean {
    return ((this.tableType || '').toUpperCase() === 'DELIVERY');
  }
}
